import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Bell, Sparkles, Mail, Megaphone, CheckCheck, ChevronRight } from "lucide-react";

const KIND_ICONS = { verdetto: Sparkles, virginia: Mail, test: Megaphone };

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

export default function Notifiche() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/notifications").then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/push/enabled").then((r) => setPushEnabled(r.data.enabled)).catch(() => {});
  }, []);

  const togglePush = async () => {
    const next = !pushEnabled;
    setPushEnabled(next);
    try {
      await api.post("/push/enabled", { enabled: next });
      toast.success(next ? "Notifiche push attivate." : "Notifiche push disattivate.");
    } catch {
      setPushEnabled(!next);
      toast.error("Non riesco ad aggiornare l'impostazione. Riprova.");
    }
  };

  useEffect(() => { load(); }, [load]);

  const openItem = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* noop */ }
      window.dispatchEvent(new Event("arcana-notifications-seen"));
    }
    if (n.link) navigate(n.link);
  };

  const readAll = async () => {
    try {
      await api.post("/notifications/read-all");
      window.dispatchEvent(new Event("arcana-notifications-seen"));
      load();
    } catch { /* noop */ }
  };

  const sendTest = async () => {
    try {
      await api.post("/admin/notifications/test");
      toast.success("Notifica di prova creata: la vedi in cima alla lista.");
      window.dispatchEvent(new Event("arcana-notifications-seen"));
      load();
    } catch {
      toast.error("Impossibile creare la notifica di prova.");
    }
  };

  const hasUnread = (items || []).some((n) => !n.read);

  return (
    <div className="max-w-2xl mx-auto px-4 lg:px-8 py-14 sm:py-20" data-testid="notifiche-page">
      <div className="text-center mb-10">
        <p className="eyebrow">Il tuo centro</p>
        <h1 className="font-display text-4xl sm:text-5xl text-amber-100 mt-4">Notifiche</h1>
      </div>

      <div className="mystic-card px-5 py-4 mb-8 flex items-center justify-between gap-4" data-testid="notifiche-push-settings">
        <div className="flex items-start gap-3">
          <Bell className="w-4 h-4 text-amber-400/80 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-purple-100/85">Notifiche push</p>
            <p className="text-[11px] text-purple-300/50 mt-0.5 leading-relaxed">
              Avvisi discreti sul telefono (verdetto settimanale, regali, novità). Mai contenuti delle tue letture:
              quelli restano nell'area protetta dal PIN.
            </p>
          </div>
        </div>
        <button
          onClick={togglePush}
          data-testid="notifiche-push-toggle"
          aria-label={pushEnabled ? "Disattiva notifiche push" : "Attiva notifiche push"}
          className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ${pushEnabled ? "bg-amber-500/80" : "bg-[#4a2175]"}`}
        >
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${pushEnabled ? "left-[22px]" : "left-0.5"}`} />
        </button>
      </div>

      <div className="flex items-center justify-between gap-3 mb-6">
        <p className="text-xs text-purple-300/50">
          {hasUnread ? "Le notifiche non lette sono in alto." : "Nessuna notifica da leggere."}
        </p>
        {hasUnread && (
          <button onClick={readAll} data-testid="notifiche-read-all-btn" className="text-xs text-purple-300/70 hover:text-amber-300 transition-colors inline-flex items-center gap-1.5">
            <CheckCheck className="w-3.5 h-3.5" /> Segna tutte come lette
          </button>
        )}
      </div>

      {items === null ? (
        <div className="text-center py-16 text-purple-300/50 text-sm" data-testid="notifiche-loading">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="mystic-card p-10 text-center" data-testid="notifiche-empty">
          <Bell className="w-8 h-8 text-purple-300/30 mx-auto mb-4" />
          <p className="text-purple-200/70 text-sm">Non hai ancora nessuna notifica.</p>
          <p className="text-purple-300/50 text-xs mt-2">Quando il tuo Verdetto Settimanale sarà pronto, o Virginia ti risponderà, lo troverai qui.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="notifiche-list">
          {items.map((n) => {
            const Icon = KIND_ICONS[n.kind] || Bell;
            return (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                data-testid={`notifica-${n.id}`}
                className={`w-full text-left rounded-xl border p-4 sm:p-5 flex items-start gap-3.5 transition-colors ${
                  n.read
                    ? "border-[#4a2175]/50 bg-[#180a26]/60 opacity-70 hover:opacity-100"
                    : "border-amber-500/40 bg-[#180a26] hover:border-amber-500/60"
                }`}
              >
                <span className={`mt-0.5 shrink-0 rounded-full p-2 ${n.read ? "bg-[#0f0617] text-purple-300/50" : "bg-amber-500/15 text-amber-300"}`}>
                  <Icon className="w-4 h-4" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${n.read ? "text-purple-100/70" : "text-amber-100"}`}>{n.title}</span>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" data-testid={`notifica-unread-dot-${n.id}`} />}
                  </span>
                  <span className="block text-xs text-purple-200/70 mt-1 leading-relaxed">{n.body}</span>
                  <span className="block text-[10px] text-purple-300/40 mt-1.5">{fmtDate(n.created_at)}</span>
                </span>
                <ChevronRight className="w-4 h-4 text-purple-300/40 shrink-0 mt-1" />
              </button>
            );
          })}
        </div>
      )}

      {user?.role === "admin" && (
        <div className="mt-10 pt-6 border-t border-[#4a2175]/50 text-center" data-testid="notifiche-admin-test">
          <p className="text-[10px] font-cinzel tracking-[0.2em] uppercase text-purple-300/40 mb-3">Solo amministratore</p>
          <button
            onClick={sendTest}
            data-testid="notifiche-test-btn"
            className="px-6 py-2.5 text-xs rounded-lg border border-[#4a2175] text-purple-200/80 hover:border-amber-500/40 transition-colors inline-flex items-center gap-2"
          >
            <Megaphone className="w-3.5 h-3.5" /> Invia notifica di prova
          </button>
        </div>
      )}
    </div>
  );
}
